import { useEffect, useState } from 'react';
import { parseFeishuTitles } from '@/api/feishu';
import { extractFeishuUrls, normalizeFeishuUrl } from '@/utils/feishuLink';

const MAX_BATCH = 50; // 后端单请求上限

// 跨组件、跨导航共享的标题缓存。
// 值为标题 = 解析成功；值为 null = 已请求但失败（不再重试，避免请求风暴）。
const titleCache = new Map<string, string | null>();
// 飞行中的请求，避免同一 URL 并发重复请求
const inflight = new Map<string, Promise<void>>();

const fetchTitles = (urls: string[]): Promise<void> => {
  const missing = urls.filter((u) => !titleCache.has(u) && !inflight.has(u));
  if (missing.length) {
    const batch = missing.slice(0, MAX_BATCH);
    const promise = parseFeishuTitles(batch)
      .then((res) => {
        res.results.forEach((r) => {
          titleCache.set(normalizeFeishuUrl(r.url), r.success && r.title ? r.title : null);
        });
      })
      .catch(() => {
        // parseFeishuTitles 内部已降级，这里只兜底
      })
      .finally(() => {
        // 后端没回、或请求异常的 URL 一律标记失败，避免无限重试
        batch.forEach((u) => {
          if (!titleCache.has(u)) titleCache.set(u, null);
          inflight.delete(u);
        });
      });
    batch.forEach((u) => inflight.set(u, promise));
  }
  return Promise.all(urls.map((u) => inflight.get(u) ?? Promise.resolve())).then(() => undefined);
};

/**
 * 解析一组「内容」文本里的飞书文档标题。
 * @param texts 可能内嵌飞书链接的文本
 * @returns titles: 归一化 URL -> 标题；loading: 是否仍有请求在途
 */
export function useFeishuTitles(texts: (string | null | undefined)[]): {
  titles: Record<string, string>;
  loading: boolean;
} {
  // 拼成字符串做依赖，texts 每次渲染都是新数组引用，直接依赖它会反复重发请求
  const key = Array.from(
    new Set(texts.flatMap((t) => (t ? extractFeishuUrls(t).map(normalizeFeishuUrl) : []))),
  ).join('\n');

  const [, forceRender] = useState(0);

  useEffect(() => {
    const urls = key ? key.split('\n') : [];
    if (!urls.length) return;
    let cancelled = false;
    const bump = () => {
      if (!cancelled) forceRender((n) => n + 1);
    };
    fetchTitles(urls).then(bump, bump);
    return () => {
      cancelled = true;
    };
  }, [key]);

  const titles: Record<string, string> = {};
  let loading = false;
  if (key) {
    for (const u of key.split('\n')) {
      const cached = titleCache.get(u);
      if (cached) titles[u] = cached;
      else if (cached === undefined) loading = true;
    }
  }
  return { titles, loading };
}

/** 仅供单元测试：清空模块级缓存与状态。 */
export function resetFeishuTitleCache(): void {
  titleCache.clear();
  inflight.clear();
}
